import packageJson from '../../../../package.json';
import { AllocationImplementation } from '../app/shared/models/allocation-implementation.enum';
import { AllocationMethod } from '../app/shared/models/allocation-method.enum';
import { DefaultMetricsGroup } from '../app/shared/models/default-metrics-group.enum';


export const environment: {
    appName: string;
    test: boolean;
    debug: boolean;
    apiUrl: string;
    production: boolean;
    attributes: boolean;
    listingPagination: boolean;
    envName: string;
    version: string;
    userManagementApiUrl: string;
    pictureManagerApiUrl: string;
    awsCognitoIdentityPoolId: string;
    awsUserPoolsId: string;
    awsUserPoolsWebClientId: string;
    awsOAuthDomain: string;
    awsOAuthRedirectSignIn: string;
    awsOAuthRedirectSignOut: string;
    awsOAuthScopes: Array<string>;
    pictureManagerCloudfrontDistribution: string;
    packlotEnabled: boolean;
    /**
     * Description for stylingMetricsSplit
     *
     * Default: false
     * BRD: true
     *
     * When stylingMetricsSplit is true, the getSizeValue function, in the styling component, splits the string
     * and returns the number.
     * Associated code: return Number(String(item[size]).split('|')[0]);
     *
     * When stylingMetricsSplit is false, the getSizeValue function, in the styling component, just returns the item size.
     * Associated code: return item[size];
     */
    stylingMetricsSplit: boolean;

    /**
     * Description for stylingExcludeStoreNumber
     *
     * Default: false
     * BRD: true
     *
     * When stylingExcludeStoreNumber is set to true, the getStoreName function, in the styling component, returns the
     * store name WITHOUT the brackets and id.
     * Associated code: return item.store_name.substr(0, item.store_name.indexOf('(')).trim();
     *
     * When stylingExcludeStoreNumber is set to false, the getStoreName function, in the styling component, returns the
     * store name WITH the brackets and id.
     * Associated code: return item.store_name.substr(item.store_name.indexOf(')') + 2).trim();
     */
    stylingExcludeStoreNumber: boolean;

    /**
     * Description for allocationMethod
     *
     * Duns: ExactMl
     * BRD: ExactAlgo
     * Default: ExactAlgo
     *
     * This dictates the allocation method used in the determineAllocationColumn, in the workbench table component.
     * Associated code: return `allocated_${environment.allocationMethod}_${data.weeks_of_cover}wk_sls_u`;
     *                  return `allocated_${environment.allocationMethod}_${parameters.weeksOfCover}wk_sls_u`;
     *                  return 'allocated_${environment.allocationMethod}_${environment.allocationMethodNumberOfWeeks}wk_sls_u';
     */
    allocationMethod: AllocationMethod;

    /**
     * Description for allocationDefaultNumberOfWeeks
     *
     * Default: 8
     *
     * This dictates the number of weeks for the allocation method used in the determineAllocationColumn, in the workbench table component.
     * Associated code: return 'allocated_${environment.allocationMethod}_${environment.allocationMethodNumberOfWeeks}wk_sls_u';
     */
    allocationDefaultNumberOfWeeks: number;
    defaultMetricsGroup: DefaultMetricsGroup;
    canCreateAllocations: boolean;
    nextFrontendUrl: string;
    expansiveWeeksOfCover: boolean;
    AllocationImplementation: AllocationImplementation;
    allocationFlushOutNumberOfWeeks: number;
    allocationHasFlushOut: boolean;
    excludeZeroWeeksOfCover: boolean;
    onlyHasAllocations: boolean;
    useBrandivioBranding: boolean;
    allowAllocationCompleteAll: boolean;
    allowFileIntegrationCompleteManually: boolean;
    allowFileIntegrationRejectManually: boolean;
    allowSyncStartedManually: boolean;
    useWWAlternateFilterDisplayNames: boolean;
    usePnPAlternateFilterDisplayNames: boolean;
    allowSso: boolean;
    hideAnalyticsFiltersErrors: boolean;
    allowGlobalWocOverride: boolean;
    /**
     * Description for stylingExportFormat
     *
     * Default: 'xlsx'
     * ttown: 'csv'
     *
     * Controls the export format used in the exportStyling method of the styling component.
     * 'xlsx' uses ExcelExportService.exportToExcel (ExcelJS workbook).
     * 'csv' uses ExcelExportService.exportToCsv (plain comma-separated values).
     */
    stylingExportFormat: 'xlsx' | 'csv';
    hasCommaSeparatedThousands: boolean;
    hasShortenedAndFormattedRandValues: boolean;
    hasShortenedAndFormattedPercentages: boolean;
    hasShortenedThousandRandValues: boolean;
    hasShortenedMillionAndGreaterRandValues: boolean;
    hasShortenedAndFormattedUnitValues: boolean;
} = {
    appName: 'Lynkd Pattern Analytics',
    envName: 'PROD',
    debug: false,
    production: true,
    test: false,
    attributes: true,
    listingPagination: false,
    version: packageJson.version,
    apiUrl: 'https://sipafcut1e.execute-api.af-south-1.amazonaws.com/prod-v5-2/app',
    userManagementApiUrl: 'https://pnp-prod-um.patternretail.com',
    pictureManagerApiUrl: 'https://sipafcut1e.execute-api.af-south-1.amazonaws.com/prod-v5-2/app/api/picture-manager',
    awsCognitoIdentityPoolId: 'eu-west-1:31970a3e-5bf1-426e-aab3-7e922927f835',
    awsUserPoolsId: 'eu-west-1_CjJA19LKs',
    awsUserPoolsWebClientId: '7nk894nvuv20baeqi43rhformm',
    awsOAuthDomain: 'pa-pnp-uat.auth.eu-west-1.amazoncognito.com',
    awsOAuthRedirectSignIn: 'https://pnp-prod-pa.patternretail.com/auth',
    awsOAuthRedirectSignOut: 'https://pnp-prod-pa.patternretail.com/auth',
    awsOAuthScopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
    allowSso: true,
    pictureManagerCloudfrontDistribution: 'https://d21k4vjjj5iod4.cloudfront.net',
    packlotEnabled: true,
    stylingMetricsSplit: true,
    stylingExcludeStoreNumber: false,
    allocationMethod: AllocationMethod.ExactAlgo,
    allocationDefaultNumberOfWeeks: 0,
    defaultMetricsGroup: DefaultMetricsGroup.Algo,
    canCreateAllocations: true,
    nextFrontendUrl: '',
    expansiveWeeksOfCover: true,
    allocationFlushOutNumberOfWeeks: 48,
    allocationHasFlushOut: true,
    onlyHasAllocations: false,
    useBrandivioBranding: false,
    useWWAlternateFilterDisplayNames: false,
    usePnPAlternateFilterDisplayNames: true,
    hideAnalyticsFiltersErrors: true,
    AllocationImplementation: AllocationImplementation.Manual,
    allowAllocationCompleteAll: true,
    allowFileIntegrationCompleteManually: true,
    allowFileIntegrationRejectManually: true,
    allowSyncStartedManually: true,
    allowGlobalWocOverride: false,
    excludeZeroWeeksOfCover: false,
    stylingExportFormat: 'xlsx',
    hasCommaSeparatedThousands: false,
    hasShortenedAndFormattedRandValues: false,
    hasShortenedAndFormattedPercentages: false,
    hasShortenedThousandRandValues: false,
    hasShortenedMillionAndGreaterRandValues: false,
    hasShortenedAndFormattedUnitValues: false
};
